import { useState, useEffect } from 'react';
import { ShoppingBag } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { usePets } from '../../../contexts/PetsContext';
import { useToast } from '../../../contexts/ToastContext';
import { formatGrams } from '../../../lib/format';
import { VALIDATION } from '../../../config/constants';

/**
 * "Compré bolsas": suma bolsas cerradas a la reserva del grupo.
 * Si el grupo nunca tuvo bagCapacityGr seteado, el modal exige el dato
 * la primera vez. Si ya estaba seteado, se pre-rellena y es editable
 * (por si cambia de marca/tamaño).
 */
const AddExtraBagsModal = ({ group, isOpen, onClose, onSuccess }) => {
  const { addExtraBags } = usePets();
  const toast = useToast();

  const isFirstTime = group.bagCapacityGr == null;
  // Si hay bolsas en reserva con capacidad ya registrada, no permitimos cambiarla
  // (rompería el inventario: las viejas pasarían a "valer" el nuevo tamaño).
  const capacityLocked = !isFirstTime && (group.extraBagsCount ?? 0) > 0;

  const [count, setCount] = useState('1');
  const [bagCapacityGr, setBagCapacityGr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setCount('1');
      setBagCapacityGr(group.bagCapacityGr != null ? String(group.bagCapacityGr) : '');
      setError(null);
    }
  }, [isOpen, group.bagCapacityGr]);

  const countNum = Number(count);
  const capacityNum = Number(bagCapacityGr);

  // bagCapacityGr es obligatorio si NUNCA se setteó.
  // Si ya estaba seteado, lo mandamos solo si el usuario lo cambió Y no está locked.
  const capacityChanged = isFirstTime || (!capacityLocked && capacityNum !== group.bagCapacityGr);

  const countError = (() => {
    if (!count) return 'Requerido';
    if (!Number.isInteger(countNum) || countNum <= 0) return 'Debe ser un entero positivo';
    return null;
  })();

  const capacityError = (() => {
    if (isFirstTime && !bagCapacityGr) return 'Requerido la primera vez';
    if (!bagCapacityGr) return null;
    if (
      !Number.isInteger(capacityNum) ||
      capacityNum < VALIDATION.BAG_CAPACITY_MIN_GR ||
      capacityNum > VALIDATION.BAG_CAPACITY_MAX_GR
    ) {
      return `Debe estar entre ${formatGrams(VALIDATION.BAG_CAPACITY_MIN_GR)} y ${formatGrams(VALIDATION.BAG_CAPACITY_MAX_GR)}`;
    }
    return null;
  })();

  const isValid = !countError && !capacityError;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!isValid) return;
    try {
      setSubmitting(true);
      setError(null);
      await addExtraBags(group.id, {
        count: countNum,
        bagCapacityGr: capacityChanged ? capacityNum : undefined,
      });
      toast.success(
        countNum === 1
          ? `Bolsa agregada a la reserva de ${group.name}.`
          : `${countNum} bolsas agregadas a la reserva de ${group.name}.`
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'No se pudo registrar la compra.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => !submitting && onClose()} title="Compré bolsas">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <p className="text-sm text-slate-600">
          {isFirstTime
            ? 'Primera vez: indicá cuánto pesa cada bolsa cerrada. Las próximas veces vamos a recordar el dato.'
            : 'Sumá las bolsas cerradas que compraste. Cuando se vacíe la principal, vas a poder "abrir bolsa" desde la reserva.'}
        </p>

        <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Reserva actual</span>
            <span className="font-medium text-slate-900">
              {group.extraBagsCount ?? 0} bolsa{(group.extraBagsCount ?? 0) === 1 ? '' : 's'}
            </span>
          </div>
          {group.bagCapacityGr != null && (
            <div className="mt-1.5 flex items-center justify-between">
              <span className="text-slate-500">Cada bolsa</span>
              <span className="font-medium text-slate-900">{formatGrams(group.bagCapacityGr)}</span>
            </div>
          )}
        </div>

        <Input
          label="¿Cuántas bolsas?"
          type="number"
          inputMode="numeric"
          value={count}
          onChange={(e) => setCount(e.target.value)}
          error={countError}
          min={1}
          max={VALIDATION.EXTRA_BAGS_MAX}
        />

        <Input
          label={
            isFirstTime
              ? 'Capacidad de cada bolsa (gramos)'
              : capacityLocked
                ? 'Capacidad de cada bolsa'
                : 'Capacidad de cada bolsa (editable)'
          }
          type="number"
          inputMode="numeric"
          value={bagCapacityGr}
          onChange={(e) => setBagCapacityGr(e.target.value)}
          error={capacityError}
          placeholder="Ej: 15000"
          hint={
            capacityLocked
              ? 'Para registrar un tamaño distinto, primero usá las bolsas que ya tenés en reserva'
              : !isFirstTime
                ? 'Si cambiaste de marca o tamaño, ajustalo'
                : null
          }
          disabled={capacityLocked}
          min={VALIDATION.BAG_CAPACITY_MIN_GR}
          max={VALIDATION.BAG_CAPACITY_MAX_GR}
        />

        {error && (
          <p className="text-sm text-status-red bg-red-50 rounded-lg px-3 py-2">{error}</p>
        )}

        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            fullWidth
            leftIcon={ShoppingBag}
            isLoading={submitting}
            disabled={!isValid || submitting}
          >
            Sumar a la reserva
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddExtraBagsModal;
